import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { envelopeBudgetsApi } from '../../services/api';

interface BudgetProgressData {
  category_id: number;
  category_name: string;
  category_color: string;
  budgeted_amount: number;
  spent_amount: number;
  remaining_amount: number;
  progress_percentage: number;
  status: 'on_track' | 'warning' | 'over_budget';
  days_left_in_month: number;
  projected_spend: number;
}

interface CircularProgressRingProps {
  data: BudgetProgressData;
  size: number;
}

const CircularProgressRing: React.FC<CircularProgressRingProps> = ({ data, size }) => {
  const theme = useTheme();
  
  const getStatusColor = (status: string, percentage: number) => {
    switch (status) {
      case 'on_track':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'over_budget':
        return theme.palette.error.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'on_track':
        return <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'warning':
        return <Warning sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'over_budget':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k`;
    }
    return Math.round(amount).toString();
  };

  const progressValue = Math.min(data.progress_percentage, 100);

  return (
    <Card 
      sx={{ 
        height: '100%',
        minHeight: 180,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 2,
        height: '100%',
        position: 'relative'
      }}>
        {/* Progress Ring */}
        <Box sx={{ position: 'relative', mb: 1 }}>
          {/* Background circle */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={size}
            thickness={6}
            sx={{
              color: theme.palette.grey[200],
              position: 'absolute',
            }}
          />
          {/* Progress circle */}
          <CircularProgress
            variant="determinate"
            value={progressValue}
            size={size}
            thickness={6}
            sx={{
              color: getStatusColor(data.status, data.progress_percentage),
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          {/* Center content */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              {Math.round(data.progress_percentage)}%
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
              spent
            </Typography>
          </Box>
        </Box>

        {/* Category Info */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mb: 0.5 }}>
            <Box 
              sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                bgcolor: data.category_color || theme.palette.primary.main 
              }} 
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {data.category_name}
            </Typography>
          </Box>
          
          {/* Budget amounts */}
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            CHF {formatCurrency(data.spent_amount)} / CHF {formatCurrency(data.budgeted_amount)}
          </Typography>
          
          {/* Remaining amount */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
            {data.remaining_amount >= 0 ? (
              <>
                <TrendingUp sx={{ fontSize: 14, color: 'success.main' }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'success.main' }}>
                  CHF {formatCurrency(data.remaining_amount)} left
                </Typography>
              </>
            ) : (
              <>
                <TrendingDown sx={{ fontSize: 14, color: 'error.main' }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'error.main' }}>
                  CHF {formatCurrency(Math.abs(data.remaining_amount))} over
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Status indicator */}
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          {getStatusIcon(data.status)}
        </Box>
      </CardContent>
    </Card>
  );
};

const BudgetProgressRings: React.FC = () => {
  const [data, setData] = useState<BudgetProgressData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get current month and year
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
        const currentYear = currentDate.getFullYear();
        
        // Fetch envelope budget summary for current month
        const budgetSummary = await envelopeBudgetsApi.getEnvelopeBudgetSummary(currentYear, currentMonth);
        
        // Transform API data to BudgetProgressData format
        const budgetProgressData: BudgetProgressData[] = budgetSummary.current_period.budget_allocations.map(allocation => {
          const progressPercentage = allocation.allocated_amount > 0 
            ? (allocation.spent_amount / allocation.allocated_amount) * 100 
            : 0;
          
          const remainingAmount = allocation.allocated_amount - allocation.spent_amount;
          
          let status: 'on_track' | 'warning' | 'over_budget';
          if (progressPercentage > 100) {
            status = 'over_budget';
          } else if (progressPercentage > 85) {
            status = 'warning';
          } else {
            status = 'on_track';
          }
          
          // Calculate days left in current month
          const lastDayOfMonth = new Date(currentYear, currentMonth, 0).getDate();
          const daysLeftInMonth = lastDayOfMonth - currentDate.getDate();
          
          return {
            category_id: allocation.category_id,
            category_name: allocation.category?.name || 'Unknown Category',
            category_color: allocation.category?.color || '#3498db',
            budgeted_amount: allocation.allocated_amount,
            spent_amount: allocation.spent_amount,
            remaining_amount: remainingAmount,
            progress_percentage: progressPercentage,
            status,
            days_left_in_month: Math.max(0, daysLeftInMonth),
            projected_spend: allocation.spent_amount // Simple projection for now
          };
        });

        setData(budgetProgressData);
        setError(null);
      } catch (err) {
        setError('Failed to load budget progress data');
        console.error('Error fetching budget progress:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          No budget data available. Set up your budgets to see progress rings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ 
          color: 'primary.main', 
          fontWeight: 'bold',
          fontSize: { xs: '1rem', md: '1.25rem' }
        }}>
          Budget Progress This Month
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
          Track your spending against budgeted amounts
        </Typography>
      </Box>

      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { 
          xs: 'repeat(2, 1fr)', 
          sm: 'repeat(3, 1fr)', 
          md: 'repeat(4, 1fr)' 
        },
        gap: { xs: 2, md: 3 }
      }}>
        {data.map((budgetData) => (
          <Box key={budgetData.category_id}>
            <CircularProgressRing 
              data={budgetData} 
              size={isMobile ? 80 : 100} 
            />
          </Box>
        ))}
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
              {data.filter(d => d.status === 'on_track').length}
            </Typography>
            <Typography variant="caption" color="text.secondary">On Track</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
              {data.filter(d => d.status === 'warning').length}
            </Typography>
            <Typography variant="caption" color="text.secondary">Warning</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
              {data.filter(d => d.status === 'over_budget').length}
            </Typography>
            <Typography variant="caption" color="text.secondary">Over Budget</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BudgetProgressRings;